import type { Recipe, RecipeListRow } from '@ctcells/contract'

import { dateTimeText } from '../format.ts'

import { isNewFormatRecipe, newFormatFacts } from './recipeStandardModel.ts'

/**
 * `Recipe`(계약) → `RecipeListRow`(화면) 변환.
 *
 * 🔴 **표시값만 만든다.** 버튼 활성에 쓰이는 값은 한 글자도 만들지 않는다.
 *
 * 🔴 **`description` 을 되살리지 마라**(2026-09-21 제거). `Recipe` 에 설명 필드가 «없고»
 *    (실측 17속성 전수) 쓰기 본문(`RecipeWriteEnvelope`)에도 없다. 종전에는 여기서
 *    `description: t('mask.none')` 을 **박아** 내보냈고, 목록이 이름 아랫줄에 그려서
 *    **전 행이 «항상» `—`** 였다. 「응답에 없는 값이면 그 자리를 만들지 않는다」(eric 확정) —
 *    `usage_count`·「수정 시각」과 같은 축이다. 필요해지면 **API 가 먼저** 생겨야 한다.
 *
 * 🔴 **「수정」 열은 2026-09-20 에 「등록」으로 바뀌었다.** `Recipe` 에 `updated_at` 이 없고
 *    실물이 주는 것은 `created_at`·`created_by` 다. 예전에는 「수정」 헤더 아래에 `—` 와
 *    **작성자**가 나란히 찍혀 뜻이 어긋나 있었다. 이 도메인은 재커밋으로 version 이 오르는
 *    모델이라 「수정 시각」이라는 축 자체가 없다 — 되돌리지 마라.
 *
 * ⚠ 스텝 수는 `payload` 에서 **센다.** 서버 집계가 아니라 본문 파생값이라, 본문을 안 싣는
 *   축약 응답(`fields=`)이 생기면 같이 사라진다. 그때는 서버가 세어 줘야 한다.
 */
export function toRecipeListRow(recipe: Recipe): RecipeListRow {
  const payload = recipe.payload

  return {
    recipe_id: recipe.recipe_id,
    name: recipe.name,
    status: recipe.status,
    version: recipe.version,
    step_counts: {
      centrifuge: payload.centrifuge?.steps?.length ?? 0,
      assembler: payload.assembler?.steps?.length ?? 0,
      staining: payload.staining?.steps.length ?? 0,
    },
    // 🔴 새 형식이면 명령 수(없으면 null) — 옛 `step_counts` 0 을 «없다»로 그리지 않게
    ...(isNewFormatRecipe(payload)
      ? { new_format_commands: newFormatFacts(payload).commands ?? null }
      : {}),
    created_text: dateTimeText(recipe.created_at),
    // 🔴 원값 그대로(이름표를 찾는 열쇠다). 빈 글자는 「기록 안 됨」과 같다
    created_by: recipe.created_by == null || recipe.created_by === '' ? null : recipe.created_by,
    /*
     * 🔴 **정렬용 원값을 같이 싣는다**(2026-09-21). 목록의 「최근 등록」 정렬이 종전에는
     *    `return 0` 이었다 — 고를 수는 있는데 아무 일도 안 나는 **죽은 옵션**이었고,
     *    이 줄에 정렬할 «수»가 없어서 그랬다. 표기(`created_text`)로는 못 센다.
     * ⚠ 값이 없으면 키를 싣지 않는다 — `exactOptionalPropertyTypes` 가 켜져 있고,
     *   무엇보다 「모르는 시각」을 빈 문자열로 접으면 정렬이 그것을 «가장 오래된 것»으로 읽는다.
     */
    ...(recipe.created_at === undefined ? {} : { created_at: recipe.created_at }),
  }
}

/** 생성 시각 표기가 필요해지면 이것을 쓴다 — 지금은 「수정」 열에 넣지 않는다 */
export function recipeCreatedText(recipe: Recipe): string {
  return dateTimeText(recipe.created_at)
}

/**
 * 레시피 상세 **서랍 안 표들**의 열이 빠지는 차례(eric 2026-09-30 「레시피도.. 해당 열을 클릭하면.. 1/3만 열리게 해줘」).
 * 서랍이 1/3 로 좁아지면 표가 카드를 넘친다(1280 실측: 랩웨어 표 +488px · 염색 스텝 표 +228px). 판정은 공용 표
 * (`DataTable` · `columnFit.ts`)가 하고, 여기는 «무엇을 먼저»만 정한다. 숫자가 낮을수록 먼저 빠진다. **여기 없는 열은 늘 보인다.**
 *
 * 🔴 **빠진 열의 값은 행을 펼치면 선다**(`revealHidden`) — 서랍 안의 표는 값이 갈 다른 곳이 없다.
 * 고른 기준: 줄을 가르는 첫 열(ID · 이름 · 채널 · 단계)과 동작은 끝까지 남는다. 긴 식별자 · 보조 값부터 빠진다.
 * ⚠ 전체 페이지(장비 관점 콘솔)는 넓어서 거의 빠지지 않는다 — 빠지더라도 같은 차례다.
 */
export const RECIPE_TABLE_PRIORITY = {
  /** 이름은 남는다. 연결 대상(점 경로 · 길다) → 종류 → 필수 */
  parameters: { binds: 1, type: 2, mandatory: 3 },
  /** 정의 ID · 이름은 남는다. 상세 수준 → 기본 용량 → 웰 → 격자 → 자리 → 종류 */
  labware: { layout: 1, volume: 2, wells: 3, grid: 4, location: 5, type: 6 },
  /** 이름은 남는다. 그 밖의 값 → 기반 */
  liquidClasses: { rest: 1, base: 2 },
  /** 채널 · 시약은 남는다. 자리(긴 식별자) → 웰 → 용량 → lot → 성분 */
  reagents: { bay: 1, well: 2, volume: 3, lot: 4, component: 5 },
  /** 단계는 남는다. 비고 → 회차 → 순서 */
  moduleSteps: { note: 1, round: 2, sequence: 3 },
  /** 단계 · 동작은 남는다. 폐액 분류 → 폐기 → 셰이킹 → 액체 클래스 → 팁 정책 → 웰 → 용량 → 채널 */
  stainingSteps: {
    waste: 1,
    towaste: 2,
    shake: 3,
    liquid: 4,
    tip: 5,
    wells: 6,
    volume: 7,
    channel: 8,
  },
} as const satisfies Record<string, Record<string, number>>

/**
 * 랩웨어 표 「정의 ID」의 **줄인 꼴** — 여섯 열이 다 빠지고도 넘칠 때만(차례 7 · 위 `labware` 뒤) 끝 글자만 남긴다.
 * 끝 조각이 서로 가른다(`…ambient.1` · `…cold.2`). 전체 값은 `ShortId` 툴팁 · 보조기술 · 행 펼침에 남는다.
 */
export const RECIPE_LABWARE_ID_COMPACT = { priority: 7, tail: 12 } as const

/** 열 정의 묶음에 위 표의 차례를 얹는다. 표에 없는 열은 그대로(늘 보인다) */
export function withRecipeTablePriority<C extends { key: string }>(
  table: keyof typeof RECIPE_TABLE_PRIORITY,
  columns: readonly C[],
): (C & { priority: number | undefined })[] {
  const order: Record<string, number> = RECIPE_TABLE_PRIORITY[table]
  return columns.map((column) => ({ ...column, priority: order[column.key] }))
}
